import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, Form, Icon, Input } from 'semantic-ui-react';
import Toolbar from 'components/common/Toolbar';
import './addMicroLoan.scss';
import { toDropDownOptions } from 'utils/dropdownOptions';
import groupTypeOptions from 'constants/options/groupTypeOptions';
import ConfirmPinModal from 'components/common/modals/ConfirmPinModal';
import addMicroloansSettings from 'redux/actions/settings/addMicroloansSettings';

const walletTypes = ['group_wallet', 'individual_wallet'];
const interestTypes = ['fixed', 'internal_plus', 'internal'];

const AddMicroLoan = () => {
  const dispatch = useDispatch();
  const { push } = useHistory();

  const [form, setForm] = useState({
    name: '',
    Basic: {
      status: true,
      disbursement: '',
      eligible_groups: [],
    },
    Eligibility: {
      age: '',
      independent: { minimum_members: '' },
      graduated: { minimum_members: '' },
      supervised: { minimum_members: '' },
      national_id: false,
      active_period: '',
      loans_cleared: '',
      group_default_rate: '',
      max_repayment_days: '',
      member_default_rate: '',
      loans_disbursed: '',
    },
    Repayment: [
      {
        id: 1,
        range_to: '',
        range_from: '',
        minimum_installments: '',
      },
    ],
    'Request Amount': [{ id: 1, amount: '' }],
    'Terms and Conditions': {
      graduated: {
        groups_savings_guaranteed: '',
        member_savings_guaranteed: '',
      },
      supervised: {
        groups_savings_guaranteed: '',
        member_savings_guaranteed: '',
      },
      independent: {
        groups_savings_guaranteed: '',
        member_savings_guaranteed: '',
      },
      micro_loan_insurance: '',
    },
    Interest: {
      interest_rate: [
        {
          id: 1,
          range_from: '',
          range_to: '',
          type: '',
          interest: '',
        },
      ],
      interest_sharing_structure: {
        independent: { save_share: '', group_share: '' },
        graduated: {
          save_share: '',
          group_share: '',
          agent_share: '',
        },
        supervised: {
          save_share: '',
          group_share: '',
          agent_share: '',
        },
      },
    },
  });

  const [openModal, setOpenModal] = useState(false);
  const [userPin, setUserPin] = useState('');

  const { loading, error } = useSelector(
    ({ settings: { addMicroloanSettings } }) => addMicroloanSettings,
  );

  const handleChange = (e, { name, value }) => {
    const keys = name.split('.');
    if (keys.length === 1) {
      return setForm({ ...form, [name]: value });
    }
    if (keys.length === 2) {
      const [section, field] = keys;
      let val = value;
      if (field === 'national_id') val = value === 'yes';
      return setForm({
        ...form,
        [section]: { ...form[section], [field]: val },
      });
    }
    if (keys.length === 3) {
      const [section, group, field] = keys;
      return setForm({
        ...form,
        [section]: {
          ...form[section],
          [group]: { ...form[section][group], [field]: value },
        },
      });
    }
  };

  // Repayment helpers
  const addRepaymentRow = () => {
    setForm({
      ...form,
      Repayment: [
        ...form.Repayment,
        {
          id: form.Repayment.length + 1,
          range_from: '',
          range_to: '',
          minimum_installments: '',
        },
      ],
    });
  };
  const removeRepaymentRow = id => {
    setForm({
      ...form,
      Repayment: form.Repayment.filter(r => r.id !== id),
    });
  };
  const updateRepaymentRow = (id, field, value) => {
    setForm({
      ...form,
      Repayment: form.Repayment.map(r =>
        r.id === id ? { ...r, [field]: value } : r,
      ),
    });
  };

  // Request Amount helpers
  const addAmountRow = () => {
    setForm({
      ...form,
      'Request Amount': [
        ...form['Request Amount'],
        { id: form['Request Amount'].length + 1, amount: '' },
      ],
    });
  };
  const removeAmountRow = id => {
    setForm({
      ...form,
      'Request Amount': form['Request Amount'].filter(
        r => r.id !== id,
      ),
    });
  };
  const updateAmountRow = (id, value) => {
    setForm({
      ...form,
      'Request Amount': form['Request Amount'].map(r =>
        r.id === id ? { ...r, amount: value } : r,
      ),
    });
  };

  // Terms and conditions helper
  const updateTermsAndConditions = (groupType, field, value) => {
    setForm({
      ...form,
      'Terms and Conditions': {
        ...form['Terms and Conditions'],
        [groupType]: {
          ...form['Terms and Conditions'][groupType],
          [field]: value,
        },
      },
    });
  };

  // Interest rate helpers
  const addInterestRow = () => {
    setForm({
      ...form,
      Interest: {
        ...form.Interest,
        interest_rate: [
          ...form.Interest.interest_rate,
          {
            id: form.Interest.interest_rate.length + 1,
            range_from: '',
            range_to: '',
            type: '',
            interest: '',
          },
        ],
      },
    });
  };
  const removeInterestRow = id => {
    setForm({
      ...form,
      Interest: {
        ...form.Interest,
        interest_rate: form.Interest.interest_rate.filter(
          r => r.id !== id,
        ),
      },
    });
  };
  const updateInterestRow = (id, field, value) => {
    setForm({
      ...form,
      Interest: {
        ...form.Interest,
        interest_rate: form.Interest.interest_rate.map(r => {
          if (r.id !== id) return r;
          if (field === 'type' && value === 'internal') {
            return { ...r, type: value, interest: '' };
          }
          return { ...r, [field]: value };
        }),
      },
    });
  };

  const updateInterestSharing = (groupType, field, value) => {
    setForm({
      ...form,
      Interest: {
        ...form.Interest,
        interest_sharing_structure: {
          ...form.Interest.interest_sharing_structure,
          [groupType]: {
            ...form.Interest.interest_sharing_structure[groupType],
            [field]: value,
          },
        },
      },
    });
  };

  const handleSubmit = () => {
    const payload = {
      password: userPin,
      type: form.name?.toLowerCase().replace(/\s/g, '_'),
      section: [
        'Basic',
        'Eligibility',
        'Repayment',
        'Request Amount',
        'Terms and Conditions',
        'Interest',
      ],
      Basic: form.Basic,
      Eligibility: { ...form.Eligibility },
      Repayment: form.Repayment.map(({ id, ...rest }) => rest),
      'Request Amount': form['Request Amount']
        .map(r => Number(r.amount))
        .filter(Boolean),
      'Terms and Conditions': form['Terms and Conditions'],
      Interest: {
        interest_rate: form.Interest.interest_rate.map(
          ({ id, range_from, range_to, interest, ...rest }) => ({
            ...rest,
            range_from: Number(range_from),
            range_to: Number(range_to),
            ...(interest !== '' && { interest: Number(interest) }),
          }),
        ),
        interest_sharing_structure: Object.fromEntries(
          Object.entries(
            form.Interest.interest_sharing_structure,
          ).map(([group, shares]) => [
            group,
            Object.fromEntries(
              Object.entries(shares).map(([k, v]) => [k, Number(v)]),
            ),
          ]),
        ),
      },
    };

    addMicroloansSettings(payload)(dispatch, push);
  };

  return (
    <>
      <ConfirmPinModal
        content="Are you sure you want to add this micro loan product?"
        openModal={openModal}
        setOpenModal={setOpenModal}
        userPin={userPin}
        setUserPin={setUserPin}
        error={{ message: error }}
        loading={loading}
        hasComplexSubmitAction
        handleSubmit={handleSubmit}
        buttonContent="Add"
      />
      <Toolbar
        header={{
          title: 'New Micro loan Product',
          navigation: true,
          onNavigate: () => push('/settings/microloans'),
        }}
        style={{
          marginLeft: '0px',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: '#fff',
        }}
      />

      <div className="add-microloan">
        <Form
          onSubmit={() => setOpenModal(true)}
          className="add-microloan__form"
        >
          <h4>Basic Details</h4>

          <Form.Input
            control={Input}
            label="Name"
            placeholder="name"
            name="name"
            value={form.name}
            onChange={handleChange}
          />

          <Form.Dropdown
            fluid
            selection
            label="Disbursement wallet type"
            placeholder="Select"
            name="Basic.disbursement"
            options={toDropDownOptions(walletTypes)}
            onChange={handleChange}
            value={form.Basic?.disbursement}
          />

          <Form.Dropdown
            fluid
            selection
            multiple
            label="Eligible group type"
            placeholder="Select"
            name="Basic.eligible_groups"
            options={toDropDownOptions(groupTypeOptions)}
            onChange={handleChange}
            value={form.Basic?.eligible_groups}
          />

          <h4>Eligibility Check</h4>

          <Form.Group widths="equal">
            <Form.Dropdown
              fluid
              selection
              label="Is national ID required"
              placeholder="Select"
              name="Eligibility.national_id"
              options={toDropDownOptions(['yes', 'no'])}
              onChange={handleChange}
              value={form.Eligibility?.national_id ? 'yes' : 'no'}
            />

            <Form.Field
              control={Input}
              type="number"
              label="Minimum age for group members"
              placeholder=""
              name="Eligibility.age"
              value={form.Eligibility?.age}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field
              control={Input}
              type="number"
              label="The period a user/group should be active (Months)"
              placeholder=""
              name="Eligibility.active_period"
              value={form.Eligibility?.active_period}
              onChange={handleChange}
            />

            <Form.Field
              control={Input}
              type="number"
              label="Maximum Repayment Days"
              placeholder=""
              name="Eligibility.max_repayment_days"
              value={form.Eligibility?.max_repayment_days}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field
              control={Input}
              type="number"
              label="Loans the user should have cleared"
              placeholder=""
              name="Eligibility.loans_cleared"
              value={form.Eligibility?.loans_cleared}
              onChange={handleChange}
            />

            <Form.Field
              control={Input}
              type="number"
              label="Individual default rate (Maximum%)"
              placeholder=""
              name="Eligibility.member_default_rate"
              value={form.Eligibility?.member_default_rate}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field
              control={Input}
              type="number"
              label="Minimum internal loans disbursed"
              placeholder=""
              name="Eligibility.loans_disbursed"
              value={form.Eligibility?.loans_disbursed}
              onChange={handleChange}
            />

            <Form.Field
              control={Input}
              type="number"
              label="Group default rate (Maximum%)"
              placeholder=""
              name="Eligibility.group_default_rate"
              value={form.Eligibility?.group_default_rate}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field
              control={Input}
              type="number"
              label="Minimum Members (Independent groups)"
              placeholder=""
              name="Eligibility.independent.minimum_members"
              value={form.Eligibility?.independent.minimum_members}
              onChange={handleChange}
            />

            <Form.Field
              control={Input}
              type="number"
              label="Minimum Members (Graduated groups)"
              placeholder=""
              name="Eligibility.graduated.minimum_members"
              value={form.Eligibility?.graduated.minimum_members}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field
              control={Input}
              type="number"
              label="Minimum Members (Supervised groups)"
              placeholder=""
              name="Eligibility.supervised.minimum_members"
              value={form.Eligibility?.supervised.minimum_members}
              onChange={handleChange}
            />
          </Form.Group>

          <h4>Repayment Range</h4>

          {form.Repayment.map((item, index) => (
            <div key={item.id} className="add-microloan__range-row">
              <div className="add-microloan__range-row__left">
                <span className="add-microloan__range-label">
                  Range (Days)
                </span>
                <Form.Group widths="equal">
                  <Input
                    type="number"
                    placeholder="From"
                    value={item.range_from}
                    onChange={(_, { value }) =>
                      updateRepaymentRow(item.id, 'range_from', value)
                    }
                  />
                  <span className="input-separator" />
                  <Input
                    type="number"
                    placeholder="To"
                    value={item.range_to}
                    onChange={(_, { value }) =>
                      updateRepaymentRow(item.id, 'range_to', value)
                    }
                  />
                </Form.Group>
              </div>
              <div className="add-microloan__range-row__right">
                <Form.Field
                  control={Input}
                  type="number"
                  label="Minimum repayment insalments"
                  placeholder="1.5"
                  value={item.minimum_installments}
                  onChange={(_, { value }) =>
                    updateRepaymentRow(
                      item.id,
                      'minimum_installments',
                      value,
                    )
                  }
                />
                {index === 0 ? (
                  <Icon
                    name="plus"
                    className="cursor"
                    onClick={addRepaymentRow}
                  />
                ) : (
                  <Icon
                    name="minus"
                    className="cursor"
                    onClick={() => removeRepaymentRow(item.id)}
                  />
                )}
              </div>
            </div>
          ))}

          <h4>Requested Amount</h4>
          {form['Request Amount']?.map((item, index) => (
            <div key={item.id} className="add-microloan__amount-row">
              <Form.Field
                control={Input}
                type="number"
                label={`${index + 1}${
                  index === 0
                    ? 'st'
                    : index === 1
                    ? 'nd'
                    : index === 2
                    ? 'rd'
                    : 'th'
                } round`}
                placeholder=""
                value={item.amount}
                onChange={(_, { value }) =>
                  updateAmountRow(item.id, value)
                }
              />
              {index === 0 ? (
                <Icon
                  name="plus"
                  className="cursor"
                  onClick={addAmountRow}
                />
              ) : (
                <Icon
                  name="minus"
                  className="cursor"
                  onClick={() => removeAmountRow(item.id)}
                />
              )}
            </div>
          ))}

          <h4>Terms and Conditions</h4>

          <h5>Independent Groups</h5>
          <Form.Group widths="equal">
            <Form.Field
              control={Input}
              type="number"
              label="Percentage guaranteed by member savings (%)"
              placeholder=""
              value={
                form['Terms and Conditions']?.independent
                  ?.member_savings_guaranteed
              }
              onChange={(_, { value }) =>
                updateTermsAndConditions(
                  'independent',
                  'member_savings_guaranteed',
                  value,
                )
              }
            />
            <Form.Field
              control={Input}
              type="number"
              label="Percentage guaranteed by group savings (%)"
              placeholder=""
              value={
                form['Terms and Conditions']?.independent
                  ?.groups_savings_guaranteed
              }
              onChange={(_, { value }) =>
                updateTermsAndConditions(
                  'independent',
                  'groups_savings_guaranteed',
                  value,
                )
              }
            />
          </Form.Group>

          <h5>Graduated Groups</h5>
          <Form.Group widths="equal">
            <Form.Field
              control={Input}
              type="number"
              label="Percentage guaranteed by member savings (%)"
              placeholder=""
              value={
                form['Terms and Conditions']?.graduated
                  ?.member_savings_guaranteed
              }
              onChange={(_, { value }) =>
                updateTermsAndConditions(
                  'graduated',
                  'member_savings_guaranteed',
                  value,
                )
              }
            />
            <Form.Field
              control={Input}
              type="number"
              label="Percentage guaranteed by group savings (%)"
              placeholder=""
              value={
                form['Terms and Conditions']?.graduated
                  ?.groups_savings_guaranteed
              }
              onChange={(_, { value }) =>
                updateTermsAndConditions(
                  'graduated',
                  'groups_savings_guaranteed',
                  value,
                )
              }
            />
          </Form.Group>

          <h5>Supervised Groups</h5>
          <Form.Group widths="equal">
            <Form.Field
              control={Input}
              type="number"
              label="Percentage guaranteed by member savings (%)"
              placeholder=""
              value={
                form['Terms and Conditions']?.supervised
                  ?.member_savings_guaranteed
              }
              onChange={(_, { value }) =>
                updateTermsAndConditions(
                  'supervised',
                  'member_savings_guaranteed',
                  value,
                )
              }
            />
            <Form.Field
              control={Input}
              type="number"
              label="Percentage guaranteed by group savings (%)"
              placeholder=""
              value={
                form['Terms and Conditions']?.supervised
                  ?.groups_savings_guaranteed
              }
              onChange={(_, { value }) =>
                updateTermsAndConditions(
                  'supervised',
                  'groups_savings_guaranteed',
                  value,
                )
              }
            />
          </Form.Group>

          <h5>Micro Insurance</h5>
          <Form.Field
            control={Input}
            type="number"
            label="Micro Insurance (%)"
            placeholder=""
            value={form['Terms and Conditions']?.micro_loan_insurance}
            onChange={(_, { value }) =>
              setForm({
                ...form,
                'Terms and Conditions': {
                  ...form['Terms and Conditions'],
                  micro_loan_insurance: value,
                },
              })
            }
          />

          <h4>Interest Details</h4>

          {form.Interest.interest_rate?.map((item, index) => (
            <div
              key={item.id}
              className="add-microloan__interest-row"
            >
              <div className="add-microloan__interest-row__left">
                <span className="add-microloan__range-label">
                  Internal Interest range (%)
                </span>
                <Form.Group widths="equal">
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="From"
                    value={item.range_from}
                    onChange={(_, { value }) =>
                      updateInterestRow(item.id, 'range_from', value)
                    }
                  />
                  <span className="input-separator" />
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="To"
                    value={item.range_to}
                    onChange={(_, { value }) =>
                      updateInterestRow(item.id, 'range_to', value)
                    }
                  />
                </Form.Group>
              </div>
              <div className="add-microloan__interest-row__right">
                <Form.Group widths="equal">
                  <Form.Dropdown
                    fluid
                    selection
                    label="Interest Type"
                    placeholder="Select"
                    options={toDropDownOptions(interestTypes)}
                    value={item.type}
                    onChange={(_, { value }) =>
                      updateInterestRow(item.id, 'type', value)
                    }
                  />
                  <Form.Field
                    control={Input}
                    type="number"
                    step="0.1"
                    label="Interest (%)"
                    placeholder=""
                    disabled={item.type === 'internal'}
                    value={
                      item.type === 'internal' ? '' : item.interest
                    }
                    onChange={(_, { value }) =>
                      updateInterestRow(item.id, 'interest', value)
                    }
                  />
                </Form.Group>
              </div>
              {index === 0 ? (
                <Icon
                  name="plus"
                  className="cursor"
                  onClick={addInterestRow}
                />
              ) : (
                <Icon
                  name="minus"
                  className="cursor"
                  onClick={() => removeInterestRow(item.id)}
                />
              )}
            </div>
          ))}

          <h5>Interest sharing structure</h5>

          <h5>Independent groups</h5>
          <Form.Group widths="equal">
            <Form.Field
              control={Input}
              type="number"
              label="SAVE Share (%)"
              placeholder=""
              value={
                form.Interest.interest_sharing_structure?.independent
                  .save_share
              }
              onChange={(_, { value }) =>
                updateInterestSharing(
                  'independent',
                  'save_share',
                  value,
                )
              }
            />
            <Form.Field
              control={Input}
              type="number"
              label="Group Share (%)"
              placeholder=""
              value={
                form.Interest.interest_sharing_structure?.independent
                  ?.group_share
              }
              onChange={(_, { value }) =>
                updateInterestSharing(
                  'independent',
                  'group_share',
                  value,
                )
              }
            />
          </Form.Group>

          <h5>Graduated groups</h5>
          <Form.Group widths="equal">
            <Form.Field
              control={Input}
              type="number"
              label="SAVE Share (%)"
              placeholder=""
              value={
                form.Interest.interest_sharing_structure?.graduated
                  ?.save_share
              }
              onChange={(_, { value }) =>
                updateInterestSharing(
                  'graduated',
                  'save_share',
                  value,
                )
              }
            />
            <Form.Field
              control={Input}
              type="number"
              label="Group Share (%)"
              placeholder=""
              value={
                form.Interest.interest_sharing_structure?.graduated
                  ?.group_share
              }
              onChange={(_, { value }) =>
                updateInterestSharing(
                  'graduated',
                  'group_share',
                  value,
                )
              }
            />
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Field
              control={Input}
              type="number"
              label="Agent Share (%)"
              placeholder=""
              value={
                form.Interest.interest_sharing_structure?.graduated
                  ?.agent_share
              }
              onChange={(_, { value }) =>
                updateInterestSharing(
                  'graduated',
                  'agent_share',
                  value,
                )
              }
            />
          </Form.Group>

          <h5>Supervised groups</h5>
          <Form.Group widths="equal">
            <Form.Field
              control={Input}
              type="number"
              label="SAVE Share (%)"
              placeholder=""
              value={
                form.Interest.interest_sharing_structure?.supervised
                  ?.save_share
              }
              onChange={(_, { value }) =>
                updateInterestSharing(
                  'supervised',
                  'save_share',
                  value,
                )
              }
            />
            <Form.Field
              control={Input}
              type="number"
              label="Group Share (%)"
              placeholder=""
              value={
                form.Interest.interest_sharing_structure?.supervised
                  ?.group_share
              }
              onChange={(_, { value }) =>
                updateInterestSharing(
                  'supervised',
                  'group_share',
                  value,
                )
              }
            />
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Field
              control={Input}
              type="number"
              label="Agent Share (%)"
              placeholder=""
              value={
                form.Interest.interest_sharing_structure?.supervised
                  ?.agent_share
              }
              onChange={(_, { value }) =>
                updateInterestSharing(
                  'supervised',
                  'agent_share',
                  value,
                )
              }
            />
          </Form.Group>

          <div className="action">
            <Button
              type="submit"
              primary
              loading={loading}
              disabled={loading}
            >
              Add
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
};

export default AddMicroLoan;
